import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Clock, Facebook, Instagram, Youtube } from 'lucide-react';
import schoolLogo from '../school_logo.jpg';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    // Handle scroll effect for glassmorphism
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const links = [
        { name: 'Home', path: '/' },
        { name: 'About', path: '/about' },
        { name: 'Academics', path: '/academics' },
        { name: 'Admissions', path: '/admissions' },
        { name: 'Gallery', path: '/gallery' },
        { name: 'Contact', path: '/contact' },
    ];

    return (
        <header className="fixed w-full top-0 z-[1000] transition-all duration-300">
            {/* Top Bar - Hidden on small screens */}
            <div className={`bg-primary text-white py-2 text-xs md:text-sm transition-all duration-300 ${scrolled ? 'h-0 opacity-0 overflow-hidden py-0' : 'h-auto opacity-100'}`}>
                <div className="container flex flex-col md:flex-row justify-between items-center gap-2">
                    <div className="flex gap-6">
                        <span className="flex items-center gap-2 hover:text-secondary transition-colors cursor-pointer"><Phone size={14} /> +91 97509 78888</span>
                        <span className="flex items-center gap-2 hover:text-secondary transition-colors cursor-pointer"><Mail size={14} /> bharathi@tcmmatricschool.in</span>
                        <span className="hidden lg:flex items-center gap-2 text-gray-400"><Clock size={14} /> Mon-Sat: 8:30am - 5:00pm</span>
                    </div>
                    <div className="flex gap-4 items-center">
                        <span className="hidden md:inline text-gray-400">Follow us:</span>
                        <div className="flex gap-3">
                            <a href="https://www.facebook.com/profile.php?id=61578302762657" target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors" aria-label="Facebook"><Facebook size={14} /></a>
                            <a href="https://www.instagram.com/tcm_matriculation_school/" target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors" aria-label="Instagram"><Instagram size={14} /></a>
                            <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors" aria-label="Youtube"><Youtube size={14} /></a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Navbar */}
            <nav className={`transition-all duration-300 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-lg py-3' : 'bg-white py-5 border-b border-gray-100'}`}>
                <div className="container flex justify-between items-center">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 group">
                        <img src={schoolLogo} alt="TCM Matric School Logo" className="w-12 h-12 md:w-14 md:h-14 object-contain shadow-sm rounded-full group-hover:scale-105 transition-transform duration-300" />
                        <div className="flex flex-col">
                            <span className="text-xl md:text-2xl font-black text-primary leading-none group-hover:text-gray-900 transition-colors">TCM MATRIC</span>
                            <span className="text-[10px] md:text-xs font-bold text-gray-500 uppercase tracking-[0.2em] group-hover:text-secondary transition-colors">School of Excellence</span>
                        </div>
                    </Link>

                    {/* Desktop Menu */}
                    <div className="hidden lg:flex items-center gap-8">
                        {links.map(link => (
                            <Link
                                key={link.name}
                                to={link.path}
                                className={`text-sm font-bold uppercase tracking-wide transition-all hover:text-secondary relative group py-2 ${location.pathname === link.path ? 'text-primary' : 'text-gray-600'
                                    }`}
                            >
                                {link.name}
                                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-secondary transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left ${location.pathname === link.path ? 'scale-x-100' : ''}`}></span>
                            </Link>
                        ))}
                        <Link to="/contact" className="ml-4 btn bg-primary text-white hover:bg-secondary hover:text-primary px-6 py-2.5 rounded-full font-bold shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5 text-sm">
                            Apply Now
                        </Link>
                    </div>

                    {/* Mobile Menu Button */}
                    <button className="lg:hidden text-primary p-2" onClick={() => setIsOpen(!isOpen)}>
                        {isOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>

                {/* Mobile Menu Dropdown */}
                <div className={`lg:hidden absolute top-full left-0 w-full bg-white border-t border-gray-100 shadow-xl transition-all duration-300 origin-top ${isOpen ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0 h-0 overflow-hidden'}`}>
                    <div className="container py-8 flex flex-col gap-4">
                        {links.map(link => (
                            <Link
                                key={link.name}
                                to={link.path}
                                onClick={() => setIsOpen(false)}
                                className={`text-lg font-bold px-4 py-3 rounded-lg hover:bg-gray-50 flex justify-between items-center ${location.pathname === link.path ? 'text-primary bg-blue-50' : 'text-gray-600'
                                    }`}
                            >
                                {link.name}
                                {location.pathname === link.path && <div className="w-2 h-2 bg-primary rounded-full"></div>}
                            </Link>
                        ))}
                        <div className="mt-4 px-4">
                            <Link to="/contact" onClick={() => setIsOpen(false)} className="block w-full text-center bg-primary text-white py-4 rounded-xl font-bold hover:bg-secondary transition-colors">
                                Apply for Admission
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>
        </header>
    );
};
export default Navbar;
